// ABOUTME: Modal for creating a new support ticket with title, description (rich text), priority, and relation fields.
// ABOUTME: Connects to the createSupportTicket API endpoint.

import { Box, ButtonBase, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import StoreOutlinedIcon from "@mui/icons-material/StoreOutlined";
import { themeSettings, tokens } from "../../../../../../theme";
import SHModal from "../../../../../../components/theme/Modal/SHModal";
import Shbutton from "../../../../../../components/theme/buttons/Shbutton";
import Subheading from "../../../../../../components/theme/text/Subheading";
import Text from "../../../../../../components/theme/text/Text";
import ButtonGroup from "../../../../../../components/theme/buttons/ButtonGroup";
import ShValidatedInput from "../../../../../../components/theme/inputs/ShValidatedInput";
import ShValidatedInputDropdown from "../../../../../../components/theme/dropdowns/ShValidatedInputDropdown";
import { DropdownBaseV2, ShDropdownQuery } from "../../../../../../components/theme/dropdowns/Dropdown";
import ChatInput from "../chat/ChatInput";
import { useTranslation } from "react-i18next";
import {
    useCreateSupportTicketMutation,
    useGetSupportTicketLabelsQuery,
} from "../../../../../../newapi/warehouse/supportTicketApiSlice";
import { useGetRelationsSelectQuery } from "../../../../../../newapi/global/relation/relationSlice";
import { useGetUsersSelectQuery } from "../../../../../../newapi/user/userSlice";
import { selectUser } from "../../../../../../newapi/user/authentication/authSlice";
import { showToastMessagePostRequest } from "../../../../../../utils/toasts";
import { buildSupportTicketSchema } from "../../../../../../utils/supportTicketSchema";
import { search_keys } from "../../../../../../utils/searchKeys";
import {
    support_ticket_linked_type_to_search_key,
    support_ticket_type,
} from "../../../../../../utils/staticEnums";
import {
    generateColorFromTitle,
    generateIconColorFromTitle,
} from "../../../../../../components/theme/TabView3/IconColorGenerator";
import { getContrastText } from "../../../../../global/branding/brandConfig";
import { hexToRgb } from "../../../../../../utils/colorConverters";
import MAddSupportTicketLinkedItem from "../../../../../../components/global/Modals/MAddSupportTicketLinkedItem";

// Assign-to targets: only servicedesk-capable users. The named filter maps to
// its privilege set server-side; admins always pass regardless of privileges.
const ASSIGNEE_FILTER = { filter: "servicedesk_assignee" };

const PRIORITY_OPTIONS = [
    { id: 'low', name: 'low' },
    { id: 'medium', name: 'medium' },
    { id: 'high', name: 'high' },
    { id: 'urgent', name: 'urgent' },
];

const TICKET_TYPE_OPTIONS = Object.values(support_ticket_type).map((v) => ({
    id: v,
    name: v,
}));

const TicketTypeOption = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const option = props.option ?? {};
    const value = option.id ?? "";
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: 1 }}>
            <Box
                sx={{
                    width: 16,
                    height: 16,
                    borderRadius: 2,
                    background: generateColorFromTitle(value, theme.palette.mode, 90),
                    border: `1px solid ${generateIconColorFromTitle(value, theme.palette.mode)}`,
                    flexShrink: 0,
                }}
            />
            <Text semibold>{t(value)}</Text>
        </Box>
    );
};

const AssigneeOption = (props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    return (
        <ButtonBase
            key={props.key}
            onClick={props.onClick}
            sx={{
                flexShrink: 0,
                height: 32,
                width: 1,
                pl: 1.5,
                pr: 0.5,
                textAlign: "left",
                display: "flex",
                justifyContent: 'flex-start',
                alignItems: "center",
                borderRadius: 2,
                background: props.isSelected
                    ? (theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100])
                    : 'transparent',
                color: props.isSelected
                    ? (theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400])
                    : colors.txt["primary"],
                fontWeight: props.isSelected ? 700 : 400,
            }}
        >
            {props.option?.name}
        </ButtonBase>
    );
};

const LabelOption = (props) => {
    const option = props.option ?? {};
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: 1 }}>
            <Box
                sx={{
                    width: 16,
                    height: 16,
                    borderRadius: 2,
                    background: option.color,
                    flexShrink: 0,
                }}
            />
            <Text semibold>{option.shortName}</Text>
            {option.longName && (
                <Text light noBreak sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {option.longName}
                </Text>
            )}
        </Box>
    );
};

const MAddSupportTicket = (props) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const descriptionDataRef = useRef({ content: null, attachments: null });
    const [createSupportTicket, { isLoading }] = useCreateSupportTicketMutation();
    const { data: relationsData } = useGetRelationsSelectQuery();
    const { data: labelsData } = useGetSupportTicketLabelsQuery();
    const currentUser = useSelector(selectUser);

    const [ticketInfo, setTicketInfo] = useState({
        title: "",
        priority: "medium",
        relationId: "",
        // ticketType: support_ticket_type.ORDER,
    });

    // Default the assignee to the agent creating the ticket — they're almost
    // always the one who'll handle it. They can pick someone else from the
    // dropdown, or clear it (id=-1) for an unassigned queue ticket.
    const [assignee, setAssignee] = useState({ id: -1, name: '' });
    useEffect(() => {
        if (currentUser?.id && assignee.id === -1) {
            setAssignee({ id: currentUser.id, name: currentUser.name || '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.id]);

    const [fieldErrors, setFieldErrors] = useState({});

    const [selectedLabels, setSelectedLabels] = useState([]);
    const [selectedLinkedItems, setSelectedLinkedItems] = useState([]);
    const [linkOpen, setLinkOpen] = useState(false);

    // Opened from a sidepanel action: start with that object linked and its relation selected.
    useEffect(() => {
        if (!props.open) return;
        if (props.initialLinkedItems?.length) {
            setSelectedLinkedItems(props.initialLinkedItems);
        }
        if (props.initialRelationId) {
            setTicketInfo((prev) => ({ ...prev, relationId: props.initialRelationId }));
        }
    }, [props.open, props.initialLinkedItems, props.initialRelationId]);

    const allLabels = labelsData?.data ?? [];
    const availableLabels = useMemo(() => {
        const assignedIds = new Set(selectedLabels.map((l) => l.id));
        return allLabels.filter((l) => !assignedIds.has(l.id));
    }, [selectedLabels, allLabels]);

    const handleAddLabel = (selected) => {
        if (selected === -1) return;
        const lbl = availableLabels[selected];
        if (!lbl) return;
        setSelectedLabels((prev) => [...prev, lbl]);
    };

    const handleRemoveLabel = (id) => {
        setSelectedLabels((prev) => prev.filter((l) => l.id !== id));
    };

    const handleSelectLinkedItem = (item) => {
        setSelectedLinkedItems((prev) => [...prev, item]);
    };

    const handleRemoveLinkedItem = (idx) => {
        setSelectedLinkedItems((prev) => prev.filter((_, i) => i !== idx));
    };

    const schema = buildSupportTicketSchema(t, { requireRelation: true });

    const handleChange = (key, value) => {
        setTicketInfo({ ...ticketInfo, [key]: value });
        setFieldErrors({ ...fieldErrors, [key]: '' });
    };

    const handleSave = async () => {
        if (isLoading) return;
        try {
            setFieldErrors({});
            await schema.validate(ticketInfo, { abortEarly: false });

            const { content, attachments } = descriptionDataRef.current;

            const formData = new FormData();
            formData.append('title', ticketInfo.title);
            formData.append('priority', ticketInfo.priority);
            formData.append('relationId', ticketInfo.relationId);
            formData.append('ticketType', ticketInfo.ticketType);
            if (assignee.id !== -1) {
                formData.append('assignedToId', assignee.id);
            }

            if (content) {
                formData.append('description', typeof content === 'string' ? content : JSON.stringify(content));
            }

            if (selectedLabels.length > 0) {
                formData.append('labelIds', JSON.stringify(selectedLabels.map((l) => l.id)));
            }

            if (selectedLinkedItems.length > 0) {
                formData.append('linkedItems', JSON.stringify(selectedLinkedItems));
            }

            if (attachments) {
                attachments.forEach(att => formData.append('files', att.file));
            }

            const data = await createSupportTicket(formData).unwrap();
            showToastMessagePostRequest(t, data);

            setTicketInfo({ title: "", priority: "medium", relationId: "" });
            setAssignee({ id: currentUser?.id ?? -1, name: currentUser?.name ?? '' });
            setSelectedLabels([]);
            setSelectedLinkedItems([]);
            descriptionDataRef.current = { content: null, attachments: null };
            props.handleClose();
        } catch (err) {
            if (err.name === 'ValidationError') {
                const errors = err.inner.reduce((acc, currentError) => ({
                    ...acc,
                    [currentError.path]: currentError.message
                }), {});
                setFieldErrors(errors);
            } else {
                showToastMessagePostRequest(t, { success: false, error: err.error });
            }
        }
    };

    let isStandAlone = false;
    if (window.matchMedia('(display-mode: standalone)').matches) {
        isStandAlone = true;
    }

    const relationOptions = relationsData ?? [];

    return (
        <>
        <SHModal
            open={props.open}
            onClose={props.handleClose}
            sx={{ body: { maxWidth: 720,minWidth:720, } }}
        >
            <Box
                sx={{
                    textAlign: "center",
                    paddingY: 4,
                    paddingX: 5,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <Subheading>{t("add_support_ticket")}</Subheading>
            </Box>

            {/* BODY */}
            <Box
                sx={{
                    background: theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0],
                    padding: 3,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                }}
            >
                {/* Relation */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, height: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("relation")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, borderRadius: 4, display: "flex" }}>
                        <ShValidatedInputDropdown
                            name="relationId"
                            displayName="name"
                            valueKey="id"
                            options={relationOptions}
                            selected={ticketInfo.relationId}
                            onChange={handleChange}
                            noSelection={t("select_relation")}
                            error={fieldErrors.relationId}
                        />
                    </Box>
                </Box>

                {/* Assignee */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, height: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("assignee")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        <ShDropdownQuery
                            name="name"
                            valueKey="id"
                            dataKey={null}
                            content={useGetUsersSelectQuery}
                            config={ASSIGNEE_FILTER}
                            selected={assignee.id}
                            shComponent={<AssigneeOption />}
                            onChange={(selected, object) => {
                                setAssignee(
                                    selected !== null && object
                                        ? { id: object.id, name: object.name }
                                        : { id: -1, name: '' }
                                );
                            }}
                            closeOnSelection
                        >
                            {({ onClick }) => (
                                <ButtonBase
                                    onClick={onClick}
                                    sx={{
                                        height: 32,
                                        flex: 1,
                                        borderRadius: 4,
                                        background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "flex-start",
                                        px: 1.5,
                                    }}
                                >
                                    {assignee.id !== -1 ? (
                                        <Text semibold>{assignee.name}</Text>
                                    ) : (
                                        <Text light>{t("unassigned")}</Text>
                                    )}
                                </ButtonBase>
                            )}
                        </ShDropdownQuery>
                        {assignee.id !== -1 && (
                            <ButtonBase
                                onClick={() => setAssignee({ id: -1, name: '' })}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    flexShrink: 0,
                                    borderRadius: 3,
                                    background: theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[100],
                                    color: colors.txt["primary"],
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 14 }} />
                            </ButtonBase>
                        )}
                    </Box>
                </Box>

                {/* Ticket type */}
                <Box 
                    sx={{ 
                        width: 1, 
                        display: "flex", 
                        alignItems: "flex-start", 
                        justifyContent: "space-between", 
                        gap: 7 
                    }}>
                    <Box 
                        sx={{ 
                            width: 120, 
                            height: 32, 
                            display: "flex", 
                            alignItems: "center", 
                            flexShrink: 0 
                        }}
                    >
                        <Text light>{t("type")}</Text>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column"
                        }}
                    >
                        <DropdownBaseV2
                            displayName={"name"}
                            options={TICKET_TYPE_OPTIONS}
                            selected={TICKET_TYPE_OPTIONS.findIndex(
                                (o) => o.id === ticketInfo.ticketType
                            )}
                            onChange={(idx) => {
                                if (idx === -1) return;
                                handleChange("ticketType", TICKET_TYPE_OPTIONS[idx].id);
                            }}
                            shComponent={<TicketTypeOption />}
                            defaultStyling
                            closeOnSelection
                            disallowDeselection
                            stopPropagation
                            styles={{
                                optionSx: {
                                    paddingX: 1,
                                    pl: 0.5,
                                    py: 0,
                                    borderRadius: 3,
                                    height: 28,
                                },
                            }}
                        >
                            {({ onClick }) => {
                                const value = ticketInfo.ticketType;
                                return (
                                    <ButtonBase
                                        onClick={onClick}
                                        sx={{
                                            width: 1,
                                            height: 32,
                                            borderRadius: 4,
                                            background:
                                                theme.palette.mode === "dark"
                                                    ? colors.grey[300]
                                                    : colors.grey[100],
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-start",
                                            gap: 1,
                                            px: 1.5,
                                            ...(fieldErrors.ticketType ? {
                                                background: colors.red[100],
                                                border: `1px solid ${colors.red[400]}`,
                                            } : {}),
                                        }}
                                    >
                                        {value ? (
                                            <>
                                                <Box
                                                    sx={{
                                                        width: 16,
                                                        height: 16,
                                                        borderRadius: 2,
                                                        background: generateColorFromTitle(
                                                            value,
                                                            theme.palette.mode,
                                                            90
                                                        ),
                                                        border: `1px solid ${generateIconColorFromTitle(value, theme.palette.mode)}`,
                                                        flexShrink: 0,
                                                    }}
                                                />
                                                <Text semibold>{t(value)}</Text>
                                            </>
                                        ) : (
                                            <Text light>{t("choose_type")}</Text>
                                        )}
                                    </ButtonBase>
                                );
                            }}
                        </DropdownBaseV2>
                        {fieldErrors.ticketType &&
                            <Box sx={{ pl: 2 }}>
                                <Text bold variant="red">{fieldErrors.ticketType}</Text>
                            </Box>
                        }
                    </Box>
                </Box>

                {/* Priority */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, height: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("priority")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, borderRadius: 4, display: "flex" }}>
                        <ShValidatedInputDropdown
                            name="priority"
                            displayName="name"
                            valueKey="id"
                            options={PRIORITY_OPTIONS}
                            selected={ticketInfo.priority}
                            onChange={handleChange}
                            disallowDeselection
                            useTranslation
                        />
                    </Box>
                </Box>

                {/* Title */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, height: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("title")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, borderRadius: 4, display: "flex" }}>
                        <ShValidatedInput
                            name="title"
                            value={ticketInfo.title}
                            onChange={handleChange}
                            error={fieldErrors.title}
                        />
                    </Box>
                </Box>

                {/* Description - rich text */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, height: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("description")}</Text>
                    </Box>
                    <Box
                        sx={{
                            flex:1,
                            minWidth: 0,
                            borderRadius: 4,
                            background: theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[100],
                            p: 2,
                            minHeight: 160,
                            display: "flex",
                            flexDirection: "column",
                            pb:1,
                            px:1,
                            // width:1,
                        }}
                    >
                        <ChatInput
                            onDataChange={(data) => { descriptionDataRef.current = data; }}
                            hideSend
                            colors={colors}
                            theme={theme}
                            placeholder={t("description")}
                        />
                    </Box>
                </Box>

                {/* Labels */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, minHeight: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("labels")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 0.75, alignItems: "center", minHeight: 32 }}>
                        {selectedLabels.map((label) => {
                            const contrast = getContrastText(hexToRgb(label.color));
                            return (
                                <Box
                                    key={"slbl" + label.id}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.5,
                                        height: 24,
                                        pl: 1,
                                        pr: 0.25,
                                        borderRadius: 3,
                                        background: label.color,
                                        color: contrast,
                                        fontFamily: "Roboto, sans-serif",
                                        fontSize: 12,
                                        fontWeight: 500,
                                    }}
                                >
                                    {label.longName}
                                    <ButtonBase
                                        onClick={() => handleRemoveLabel(label.id)}
                                        sx={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: "50%",
                                            color: contrast,
                                            "&:hover": {
                                                background:
                                                    contrast === "#fff"
                                                        ? "rgba(0,0,0,0.15)"
                                                        : "rgba(255,255,255,0.25)",
                                            },
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 14 }} />
                                    </ButtonBase>
                                </Box>
                            );
                        })}
                        {availableLabels.length > 0 && (
                            <DropdownBaseV2
                                displayName={"shortName"}
                                searchOptions={{
                                    enabled: true,
                                    placeholder: t("search"),
                                    searchables: ["longName"],
                                }}
                                options={availableLabels}
                                selected={-1}
                                onChange={handleAddLabel}
                                shComponent={<LabelOption />}
                                defaultStyling
                                closeOnSelection
                                resetOnSelection
                                disallowDeselection
                                stopPropagation
                                styles={{
                                    optionSx: {
                                        paddingX: 1,
                                        pl: 0.5,
                                        py: 0,
                                        borderRadius: 3,
                                        height: 24,
                                    },
                                }}
                            >
                                {({ onClick }) => (
                                    <ButtonBase
                                        onClick={onClick}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: 3,
                                            background:
                                                theme.palette.mode === "dark"
                                                    ? colors.grey[400]
                                                    : colors.grey[100],
                                            color: colors.txt["primary"],
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <AddIcon sx={{ fontSize: 16 }} />
                                    </ButtonBase>
                                )}
                            </DropdownBaseV2>
                        )}
                    </Box>
                </Box>

                {/* Linked items */}
                <Box sx={{ width: 1, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 7 }}>
                    <Box sx={{ width: 120, minHeight: 32, display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <Text light>{t("linked_items")}</Text>
                    </Box>
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5, minHeight: 32 }}>
                        {selectedLinkedItems.map((linked, idx) => {
                            const bg = generateColorFromTitle(linked.linkedType, theme.palette.mode, 90);
                            const fg = generateIconColorFromTitle(linked.linkedType, theme.palette.mode);
                            const searchKey = support_ticket_linked_type_to_search_key[linked.linkedType];
                            const icon =
                                linked.linkedType === "webshopcredentials"
                                    ? <StoreOutlinedIcon sx={{ fontSize: 14 }} />
                                    : search_keys[searchKey]?.icon;
                            return (
                                <Box
                                    key={"sli" + idx}
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        py: 0.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: 32,
                                            flex: "0 0 auto",
                                            px: 1.5,
                                            pl: 0.5,
                                            borderRadius: 4,
                                            maxWidth: 150,
                                            minWidth: 150,
                                            background: bg,
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            overflow: "hidden",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                borderRadius: 4,
                                                height: 24,
                                                width: 24,
                                                flexShrink: 0,
                                                background: fg,
                                                color: "#fff",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                "& svg": { fontSize: 14 },
                                                "& svg path": { fill: "#fff" },
                                            }}
                                        >
                                            {icon}
                                        </Box>
                                        <Text semibold sx={{ display: "block" }} noBreak>
                                            {linked.displayReference || linked.linkedId}
                                        </Text>
                                    </Box>
                                    <Text sx={{ display: "block", flex: 1 }} noBreak>
                                        {linked.displayName}
                                    </Text>
                                    <ButtonBase
                                        onClick={() => handleRemoveLinkedItem(idx)}
                                        sx={{
                                            width: 24,
                                            height: 24,
                                            borderRadius: 3,
                                            flexShrink: 0,
                                            background: themeSettings(theme.palette.mode).palette.red.main,
                                            color: themeSettings(theme.palette.mode).palette.red.contrastText,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 14 }} />
                                    </ButtonBase>
                                </Box>
                            );
                        })}
                        <Box>
                            <ButtonBase
                                onClick={() => setLinkOpen(true)}
                                sx={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: 3,
                                    background:
                                        theme.palette.mode === "dark"
                                            ? colors.grey[400]
                                            : colors.grey[100],
                                    color: colors.txt["primary"],
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <AddIcon sx={{ fontSize: 16 }} />
                            </ButtonBase>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box
                component="footer"
                sx={{
                    display: "flex",
                    width: 1,
                    paddingBottom: isStandAlone ? "32px" : "16px",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingRight: 3,
                    zIndex: 1058,
                    position: "relative",
                    paddingTop: 2,
                    background: `${theme.palette.mode === "dark" ? colors.grey[200] : colors.grey[0]}`,
                    borderBottomLeftRadius: 24,
                    borderBottomRightRadius: 24,
                    "@media screen and (max-width: 47.9375em)": {
                        paddingRight: 0,
                    },
                }}
            >
                <Shbutton isCancel={true} className={"always-visible"} onClick={props.handleClose} color="primary" variant="text" startIcon={<ArrowRightAltIcon />}>
                    {t("cancel")}
                </Shbutton>
                <ButtonGroup version={2}>
                    <Shbutton
                        isLoading={isLoading}
                        className={"group-btn"}
                        color={theme.palette.mode === "dark" ? "blue" : "primary"}
                                                variant="contained"
                        onClick={handleSave}
                    >
                        {t("create")}
                    </Shbutton>
                </ButtonGroup>
            </Box>
        </SHModal>

        <MAddSupportTicketLinkedItem
            open={linkOpen}
            handleClose={() => setLinkOpen(false)}
            onSelect={handleSelectLinkedItem}
            relationId={ticketInfo.relationId || undefined}
            linkedItems={selectedLinkedItems}
        />
        </>
    );
};

export default MAddSupportTicket;
